import React, { useEffect, useRef, useState } from 'react';
import { StoryBible, Ledger, ChapterPlan, AppStatus } from '../src/types';
import { Trash2, Plus, ArrowUp, ArrowDown, Sparkles, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { areChapterPlansBlank } from '../src/utils/roadmapPersistence.service';
import {
  applyPremadeRoadmapParse,
  logPremadeRoadmapDiagnostic,
  parsePremadeRoadmap,
  shouldAutoApplyPremadeRoadmap,
} from '../src/utils/premadeRoadmap.service';

interface BlueprintReviewProps {
  storyBible: StoryBible;
  ledger: Ledger;
  onUpdate: (updatedBible: StoryBible, updatedLedger: Ledger) => void;
  onApproveBlueprint: () => void;
  onApproveRoadmap: () => void;
  onEnterNarrativeHub: () => Promise<void>;
  onGenerateRoadmap: () => Promise<void>;
  onBackToSetup: () => void;
  isGeneratingRoadmap: boolean;
  isEnteringHub?: boolean;
  roadmapPersistError?: string | null;
  extractionError?: string | null;
  onRetryExtraction?: () => Promise<void>;
  isAdmin?: boolean;
}

export default function BlueprintReview({
  storyBible,
  ledger,
  onUpdate,
  onApproveBlueprint,
  onApproveRoadmap,
  onEnterNarrativeHub,
  onGenerateRoadmap,
  onBackToSetup,
  isGeneratingRoadmap,
  isEnteringHub,
  roadmapPersistError,
  extractionError,
  onRetryExtraction,
}: BlueprintReviewProps) {
  const [activeTab, setActiveTab] = useState<'blueprint' | 'roadmap'>('blueprint');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [roadmapParseError, setRoadmapParseError] = useState<string | null>(null);
  const [unmatchedRoadmapBlocks, setUnmatchedRoadmapBlocks] = useState<string[]>([]);
  const premadeAutoApplyAttemptedRef = useRef(false);

  const applyParsedPremadeRoadmap = (rawText: string, reason: 'manual' | 'auto') => {
    logPremadeRoadmapDiagnostic('[PREMADE ROADMAP INPUT]', {
      rawLength: rawText.length,
      parsedChapterCount: 0,
      expectedChapterCount: storyBible.targetChapterCount,
      parsedTitles: [],
      sourceBefore: storyBible.activeRoadmapSource,
    });

    const parseResult = parsePremadeRoadmap(rawText, storyBible.targetChapterCount);
    logPremadeRoadmapDiagnostic('[PREMADE ROADMAP PARSE]', {
      rawLength: rawText.length,
      parsedChapterCount: parseResult.plans.length,
      expectedChapterCount: storyBible.targetChapterCount,
      parsedTitles: parseResult.parsedTitles,
      sourceBefore: storyBible.activeRoadmapSource,
      sourceAfter: parseResult.status === 'failure' ? storyBible.activeRoadmapSource : 'USER_ROADMAP',
      architectInvoked: false,
      parseFailureReason: parseResult.parseFailureReason,
    });

    if (parseResult.status === 'failure') {
      setRoadmapParseError(parseResult.parseFailureReason || 'Roadmap detected, but could not be parsed into chapter cards.');
      setUnmatchedRoadmapBlocks(parseResult.unmatchedRawBlocks);
      return false;
    }

    if (parseResult.countMismatch) {
      const suspectIndex = parseResult.suspectEntryIndexes?.[0];
      const suspectTitle = suspectIndex !== undefined ? parseResult.plans[suspectIndex]?.title : undefined;
      const mismatchMessage = [
        `Parsed ${parseResult.plans.length} chapters but the project expects ${storyBible.targetChapterCount}.`,
        suspectTitle ? `Suspect entry: "${suspectTitle}".` : '',
        'Fix the roadmap or update the project chapter count before approval.',
      ].filter(Boolean).join(' ');
      setRoadmapParseError(mismatchMessage);
      setUnmatchedRoadmapBlocks(parseResult.unmatchedRawBlocks);
    }

    const updatedBible = applyPremadeRoadmapParse(storyBible, parseResult);
    logPremadeRoadmapDiagnostic('[PREMADE ROADMAP STATE APPLY]', {
      rawLength: rawText.length,
      parsedChapterCount: parseResult.plans.length,
      expectedChapterCount: storyBible.targetChapterCount,
      parsedTitles: parseResult.parsedTitles,
      sourceBefore: storyBible.activeRoadmapSource,
      sourceAfter: 'USER_ROADMAP',
      architectInvoked: false,
    });
    updateBible({
      ...updatedBible,
      premadeRoadmap: rawText,
      originalUserRoadmapRaw: storyBible.originalUserRoadmapRaw || rawText,
    });
    setRoadmapParseError(null);
    setUnmatchedRoadmapBlocks(parseResult.unmatchedRawBlocks);
    if (reason === 'manual') {
      alert('Pre-made roadmap parsed successfully into editable chapter cards.');
    }
    return true;
  };

  useEffect(() => {
    if (premadeAutoApplyAttemptedRef.current) return;
    const rawText = storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap;
    if (!rawText?.trim()) return;
    if (!shouldAutoApplyPremadeRoadmap(storyBible)) return;
    premadeAutoApplyAttemptedRef.current = true;
    applyParsedPremadeRoadmap(rawText, 'auto');
  }, [storyBible.premadeRoadmap, storyBible.originalUserRoadmapRaw, storyBible.activeRoadmapSource, storyBible.chapterPlans]);

  // Helper to deep update storyBible
  const updateBible = (updates: Partial<StoryBible>) => {
    const updated = { ...storyBible, ...updates };
    onUpdate(updated, ledger);
  };

  // Helper to deep update premise
  const updatePremise = (updates: Partial<typeof storyBible.premise>) => {
    const updatedPremise = { ...storyBible.premise, ...updates };
    updateBible({ premise: updatedPremise });
  };

  // Helper to update character lists in ledger
  const updateCharacter = (name: string, fields: Record<string, any>) => {
    const updatedCharacters = { ...ledger.characters };
    if (updatedCharacters[name]) {
      updatedCharacters[name] = { ...updatedCharacters[name], ...fields };
      onUpdate(storyBible, { ...ledger, characters: updatedCharacters });
    }
  };

  const deleteCharacter = (name: string) => {
    if (window.confirm(`Are you sure you want to remove Character ${name}?`)) {
      const updatedCharacters = { ...ledger.characters };
      delete updatedCharacters[name];
      onUpdate(storyBible, { ...ledger, characters: updatedCharacters });
    }
  };

  const addCharacter = () => {
    const name = prompt("Enter Character Name:");
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    if (ledger.characters[cleanName]) {
      alert("A character with this name already exists.");
      return;
    }
    const updatedCharacters = {
      ...ledger.characters,
      [cleanName]: {
        name: cleanName,
        age: "needs confirmation",
        role: "Supporting",
        faction: "None",
        motivation: "needs confirmation",
        fear: "needs confirmation",
        relationship: "needs confirmation",
        location: "needs confirmation",
        abilities: "None",
        limitations: "None",
        canon_locks: "",
        forbidden_changes: ""
      }
    };
    onUpdate(storyBible, { ...ledger, characters: updatedCharacters });
  };

  // Factions helpers
  const updateFaction = (name: string, fields: Record<string, any>) => {
    const updatedFactions = { ...(ledger.locations || {}) }; // Store factions or locations in locations/world lore
    // Factions can be stored in the ledger as record or we can introduce a "factions" property inside ledger.
    // For backward-compatibility, we can store them inside a ledger.factions field (we will fallback to ledger.locations or keep a custom key ledger.factions which is perfectly safe and won't break standard backend because the backend only writes merged ledger).
    const factions = (ledger as any).factions || {};
    factions[name] = { ...factions[name], ...fields };
    onUpdate(storyBible, { ...ledger, factions } as any);
  };

  const deleteFaction = (name: string) => {
    if (window.confirm(`Are you sure you want to remove Faction ${name}?`)) {
      const factions = { ...((ledger as any).factions || {}) };
      delete factions[name];
      onUpdate(storyBible, { ...ledger, factions } as any);
    }
  };

  const addFaction = () => {
    const name = prompt("Enter Faction/Group Name:");
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    const factions = (ledger as any).factions || {};
    if (factions[cleanName]) {
      alert("A faction with this name already exists.");
      return;
    }
    factions[cleanName] = {
      name: cleanName,
      goal: "needs confirmation",
      public_belief: "needs confirmation",
      hidden_truth: "needs confirmation",
      leader: "needs confirmation",
      methods: "needs confirmation",
      relationship_to_protagonist: "needs confirmation",
      canon_locks: ""
    };
    onUpdate(storyBible, { ...ledger, factions } as any);
  };

  // World Rules & Lore helpers
  const updateWorldRule = (id: string, fields: Record<string, any>) => {
    const worldRules = (ledger as any).worldRules || {};
    worldRules[id] = { ...worldRules[id], ...fields };
    onUpdate(storyBible, { ...ledger, worldRules } as any);
  };

  const deleteWorldRule = (id: string) => {
    if (window.confirm("Are you sure you want to delete this lore/world rule?")) {
      const worldRules = { ...((ledger as any).worldRules || {}) };
      delete worldRules[id];
      onUpdate(storyBible, { ...ledger, worldRules } as any);
    }
  };

  const addWorldRule = () => {
    const title = prompt("Enter World Rule/Lore Title (e.g. Magic Rules, Technology Level):");
    if (!title || !title.trim()) return;
    const id = title.trim();
    const worldRules = (ledger as any).worldRules || {};
    worldRules[id] = {
      title: id,
      content: "needs confirmation"
    };
    onUpdate(storyBible, { ...ledger, worldRules } as any);
  };

  // Style rules helpers
  const getStyleRule = (field: string, defaultValue = '') => {
    const styleRules = (storyBible as any).styleRules || {};
    return styleRules[field] || defaultValue;
  };

  const updateStyleRule = (field: string, value: string) => {
    const styleRules = { ...((storyBible as any).styleRules || {}) };
    styleRules[field] = value;
    onUpdate({ ...storyBible, styleRules } as any, ledger);
  };

  // Chapter Roadmap card helpers
  const updateChapterCard = (index: number, updates: Partial<ChapterPlan>) => {
    const plans = [...(storyBible.chapterPlans || [])];
    plans[index] = { ...plans[index], ...updates };
    updateBible({ chapterPlans: plans });
  };

  const updateChapterStringArray = (index: number, field: 'requiredBeats' | 'forbiddenMoves' | 'continuityNotes', arrIndex: number, value: string) => {
    const plans = [...(storyBible.chapterPlans || [])];
    const arr = [...(plans[index][field] || [])];
    arr[arrIndex] = value;
    plans[index] = { ...plans[index], [field]: arr };
    updateBible({ chapterPlans: plans });
  };

  const addChapterStringArrayItem = (index: number, field: 'requiredBeats' | 'forbiddenMoves' | 'continuityNotes') => {
    const plans = [...(storyBible.chapterPlans || [])];
    const arr = [...(plans[index][field] || [])];
    arr.push('');
    plans[index] = { ...plans[index], [field]: arr };
    updateBible({ chapterPlans: plans });
  };

  const removeChapterStringArrayItem = (index: number, field: 'requiredBeats' | 'forbiddenMoves' | 'continuityNotes', arrIndex: number) => {
    const plans = [...(storyBible.chapterPlans || [])];
    const arr = [...(plans[index][field] || [])];
    arr.splice(arrIndex, 1);
    plans[index] = { ...plans[index], [field]: arr };
    updateBible({ chapterPlans: plans });
  };

  const moveChapterUp = (index: number) => {
    if (index === 0) return;
    const plans = [...(storyBible.chapterPlans || [])];
    const temp = plans[index];
    plans[index] = plans[index - 1];
    plans[index - 1] = temp;
    plans[index].chapterNumber = index + 1;
    plans[index - 1].chapterNumber = index;
    updateBible({ chapterPlans: plans });
  };

  const moveChapterDown = (index: number) => {
    const plans = storyBible.chapterPlans || [];
    if (index === plans.length - 1) return;
    const newPlans = [...plans];
    const temp = newPlans[index];
    newPlans[index] = newPlans[index + 1];
    newPlans[index + 1] = temp;
    newPlans[index].chapterNumber = index + 1;
    newPlans[index + 1].chapterNumber = index + 2;
    updateBible({ chapterPlans: newPlans });
  };

  const deleteChapterCard = (index: number) => {
    if (window.confirm("Are you sure you want to delete this chapter card?")) {
      const plans = (storyBible.chapterPlans || []).filter((_, i) => i !== index).map((p, i) => ({ ...p, chapterNumber: i + 1 }));
      updateBible({ chapterPlans: plans, targetChapterCount: plans.length });
    }
  };

  const addChapterCard = () => {
    const plans = [...(storyBible.chapterPlans || [])];
    plans.push({
      id: `plan_blueprint_${Date.now()}`,
      chapterNumber: plans.length + 1,
      title: `Chapter ${plans.length + 1}`,
      pov: "needs confirmation",
      summary: "needs confirmation",
      requiredBeats: ["needs confirmation"],
      forbiddenMoves: [],
      continuityNotes: [],
      status: 'planned' as const,
      updatedAt: new Date().toISOString()
    });
    updateBible({ chapterPlans: plans, targetChapterCount: plans.length });
  };

  const handleParseCustomRoadmap = () => {
    const rawText = storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap;
    if (!rawText?.trim()) return;
    try {
      applyParsedPremadeRoadmap(rawText, 'manual');
    } catch (error) {
      console.error('Roadmap parsing failed', error);
      setRoadmapParseError('Roadmap detected, but could not be parsed into chapter cards.');
    }
  };

  const charactersList = Array.isArray((storyBible as any).characterCards)
    ? (storyBible as any).characterCards
    : Object.values(ledger.characters || {});
  const factionsList = Array.isArray((storyBible as any).factions)
    ? (storyBible as any).factions
    : Object.values((ledger as any).factions || {});
  const worldRulesList = Array.isArray((storyBible as any).worldRules)
    ? (storyBible as any).worldRules
    : Object.values((ledger as any).worldRules || {});
  const hasPremadeRoadmap = Boolean(storyBible.premadeRoadmap?.trim() || storyBible.originalUserRoadmapRaw?.trim());
  const chapterPlans = storyBible.chapterPlans || [];
  const needsPremadeParse =
    hasPremadeRoadmap &&
    (chapterPlans.length === 0 || areChapterPlansBlank(chapterPlans) || storyBible.activeRoadmapSource !== 'USER_ROADMAP');

  // Diagnostic Log 5: Inside BlueprintReview.tsx render
  console.log("[DIAGNOSTIC LOG 5: BlueprintReview render]", {
    "characterCards.length": charactersList.length,
    "factions.length": factionsList.length,
    "worldRules.length": worldRulesList.length,
    "canonLocks.length": (storyBible as any).canonLocks ? (storyBible as any).canonLocks.length : 0,
    "roadmap.length": chapterPlans.length
  });

  // Diagnostic Log 4: Before rendering BlueprintReview roadmap tab
  if (activeTab === 'roadmap') {
    console.log("[DIAGNOSTIC LOG 4: Before rendering BlueprintReview roadmap tab]", {
      chapterPlansLength: chapterPlans.length,
      firstRenderedChapterPlanKeys: chapterPlans[0] ? Object.keys(chapterPlans[0]) : [],
      firstRenderedChapterPlanTitle: chapterPlans[0] ? chapterPlans[0].title : "",
      firstRenderedChapterPlanSummaryLength: chapterPlans[0]?.summary ? chapterPlans[0].summary.length : 0,
      firstRenderedChapterPlanBeatsCount: chapterPlans[0]?.requiredBeats ? chapterPlans[0].requiredBeats.length : 0
    });
  }

  // Final assertion before rendering as required
  console.log("[DIAGNOSTIC LOG: Roadmap Render Assertion]", {
    chapterPlansCount: chapterPlans.length,
    firstChapterHasTitle: !!chapterPlans[0]?.title,
    firstChapterHasSummary: !!chapterPlans[0]?.summary && chapterPlans[0]?.summary !== "needs confirmation",
    firstChapterBeatsCount: chapterPlans[0]?.requiredBeats ? chapterPlans[0].requiredBeats.length : 0
  });

  return (
    <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col p-6 overflow-y-auto">
      <div className="max-w-6xl w-full mx-auto bg-cv-panel border border-cv-border rounded-3xl p-8 shadow-2xl relative flex flex-col gap-6">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-cv-gold via-purple-500 to-cyan-500 rounded-t-3xl"></div>
        
        {/* Header */}
        <header className="flex justify-between items-start border-b border-cv-border pb-6">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tighter uppercase flex items-center gap-2">
              Review Story Blueprint
              <span className="text-xs text-cv-gold bg-cv-interactive/15 px-2.5 py-1 rounded-full border border-cv-interactive/20 font-mono tracking-widest">DRAFT</span>
            </h1>
            <p className="text-cv-muted font-mono text-[10px] mt-1 uppercase tracking-widest">
              Review, edit, and approve your story foundations before generating the chapter roadmap.
            </p>
          </div>
          <button 
            onClick={onBackToSetup}
            className="px-4 py-2 text-xs font-bold uppercase text-cv-muted hover:text-white bg-cv-elevated hover:bg-cv-elevated rounded-lg border border-cv-border-strong transition-colors"
          >
            Back to Setup
          </button>
        </header>

        {/* Tab Navigation */}
        <div className="flex border-b border-cv-border">
          <button 
            onClick={() => setActiveTab('blueprint')}
            className={`px-6 py-3 font-black text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${activeTab === 'blueprint' ? 'border-cv-interactive text-cv-gold' : 'border-transparent text-cv-muted hover:text-cv-secondary'}`}
          >
            1. Core Story Blueprint
            {storyBible.isBlueprintApproved && (
              <span className="w-4 h-4 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full flex items-center justify-center text-[9px] font-bold"><Check size={10} /></span>
            )}
          </button>
          <button 
            onClick={() => setActiveTab('roadmap')}
            className={`px-6 py-3 font-black text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${activeTab === 'roadmap' ? 'border-cv-interactive text-cv-gold' : 'border-transparent text-cv-muted hover:text-cv-secondary'}`}
          >
            2. Chapter Roadmap
            {storyBible.isRoadmapApproved && (
              <span className="w-4 h-4 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full flex items-center justify-center text-[9px] font-bold"><Check size={10} /></span>
            )}
          </button>
        </div>

        {/* Tab 1: Core Story Blueprint */}
        {activeTab === 'blueprint' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {extractionError && (
              <div className="bg-rose-950/40 border border-rose-500/30 rounded-2xl p-6 flex flex-col gap-4 text-rose-300 animate-in fade-in duration-300">
                <div className="flex items-start gap-3">
                  <span className="text-xl">⚠️</span>
                  <div className="space-y-1">
                    <h3 className="text-xs font-black uppercase tracking-wider text-rose-400">Blueprint Extraction Fault Detected</h3>
                    <p className="text-xs leading-relaxed opacity-90">{extractionError}</p>
                  </div>
                </div>
                {onRetryExtraction && (
                  <button
                    onClick={onRetryExtraction}
                    disabled={isGeneratingRoadmap}
                    className="self-start px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-rose-950/50 flex items-center gap-2 disabled:opacity-40"
                  >
                    <Sparkles size={12} />
                    {isGeneratingRoadmap ? 'Re-extracting...' : 'Retry Blueprint Extraction'}
                  </button>
                )}
              </div>
            )}

            {/* 1. Project Overview */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <h2 className="text-xs font-black text-cv-gold uppercase tracking-widest flex items-center justify-between border-b border-cv-border pb-2">
                <span>1. Project Overview</span>
                <span className="text-[9px] text-cv-disabled font-mono">EDITABLE</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Title</label>
                  <input 
                    type="text" 
                    value={storyBible.title} 
                    onChange={(e) => updateBible({ title: e.target.value })}
                    className="bg-cv-panel border border-cv-border rounded p-2 text-xs font-bold text-white focus:border-cv-interactive outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Format</label>
                  <select 
                    value={storyBible.mode} 
                    onChange={(e) => updateBible({ mode: e.target.value as any })}
                    className="bg-cv-panel border border-cv-border rounded p-2 text-xs text-cv-secondary focus:border-cv-interactive outline-none"
                  >
                    <option value="NOVEL">Novel</option>
                    {storyBible.mode === 'SHORT_STORY' && <option value="SHORT_STORY">Short Story (Legacy)</option>}
                    {storyBible.mode === 'COMIC_SCRIPT' && <option value="COMIC_SCRIPT">Comic Script (Legacy)</option>}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Target Chapters</label>
                  <input 
                    type="number" 
                    value={storyBible.targetChapterCount} 
                    onChange={(e) => updateBible({ targetChapterCount: parseInt(e.target.value) || 1 })}
                    className="bg-cv-panel border border-cv-border rounded p-2 text-xs text-white focus:border-cv-interactive outline-none font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Segment Length</label>
                  <select 
                    value={storyBible.chapterLength} 
                    onChange={(e) => updateBible({ chapterLength: e.target.value as any })}
                    className="bg-cv-panel border border-cv-border rounded p-2 text-xs text-cv-secondary focus:border-cv-interactive outline-none"
                  >
                    {storyBible.chapterLength === 'MICRO' && <option value="MICRO">Micro (~800-1200 words) (Legacy)</option>}
                    <option value="STANDARD">Standard (~2500-3500 words)</option>
                    {storyBible.chapterLength === 'EXTENDED' && <option value="EXTENDED">Extended (~4000-6000 words) (Legacy)</option>}
                  </select>
                </div>
              </div>
            </div>

            {/* 2. Core Story */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <h2 className="text-xs font-black text-cv-interactive uppercase tracking-widest flex items-center justify-between border-b border-cv-border pb-2">
                <span>2. Core Story DNA</span>
                <span className="text-[9px] text-cv-disabled font-mono">EDITABLE</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Premise & Surface Conflict</label>
                  <textarea 
                    value={storyBible.premise.surface} 
                    onChange={(e) => updatePremise({ surface: e.target.value })}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:border-cv-interactive outline-none min-h-[100px] resize-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Deeper Tension & Theme</label>
                  <textarea 
                    value={storyBible.premise.tension} 
                    onChange={(e) => updatePremise({ tension: e.target.value })}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:border-cv-interactive outline-none min-h-[100px] resize-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Permanent Change & Ending Direction</label>
                  <textarea 
                    value={storyBible.premise.change} 
                    onChange={(e) => updatePremise({ change: e.target.value })}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:border-cv-interactive outline-none min-h-[100px] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* 3. Character Cards */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center border-b border-cv-border pb-2">
                <h2 className="text-xs font-black text-cv-teal uppercase tracking-widest flex items-center gap-2">
                  <span>3. Character Cards</span>
                  <span className="text-[9px] bg-purple-900/30 text-cv-teal border border-purple-800/50 px-2 py-0.5 rounded font-mono font-bold">{charactersList.length} total</span>
                </h2>
                <button 
                  onClick={addCharacter}
                  className="px-3 py-1 bg-purple-950/50 hover:bg-purple-900/50 text-cv-teal border border-purple-800/50 hover:border-purple-600 rounded text-[10px] font-bold uppercase transition-all flex items-center gap-1.5"
                >
                  <Plus size={12} /> Add Character
                </button>
              </div>

              {charactersList.length === 0 ? (
                <div className="bg-rose-950/20 border border-rose-500/30 rounded-xl p-4 text-center text-rose-400 text-xs font-mono">
                  ⚠️ No character cards detected. Review before continuing.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {charactersList.map((char: any) => {
                    const isAgeNeedsConfirmation = char.age === "needs confirmation" || !char.age;
                    return (
                      <div key={char.name} className="bg-cv-panel border border-cv-border rounded-xl p-4 space-y-3 relative group">
                        <button 
                          onClick={() => deleteCharacter(char.name)}
                          className="absolute top-4 right-4 text-cv-danger/50 hover:text-cv-danger opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-500/10"
                          title="Delete Character"
                        >
                          <Trash2 size={14} />
                        </button>
                        
                        <div className="flex justify-between items-start pr-8">
                          <input 
                            type="text" 
                            value={char.name} 
                            onChange={(e) => {
                              const newName = e.target.value;
                              if (newName && newName !== char.name) {
                                const updatedCharacters = { ...ledger.characters };
                                const oldData = updatedCharacters[char.name];
                                delete updatedCharacters[char.name];
                                updatedCharacters[newName] = { ...oldData, name: newName };
                                onUpdate(storyBible, { ...ledger, characters: updatedCharacters });
                              }
                            }}
                            className="bg-transparent text-white font-black text-sm focus:outline-none focus:border-b focus:border-cv-interactive w-full"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Age</label>
                            <input 
                              type="text" 
                              value={char.age || ''} 
                              onChange={(e) => updateCharacter(char.name, { age: e.target.value })}
                              placeholder="needs confirmation"
                              className={`bg-cv-page border rounded p-1.5 text-xs text-white focus:outline-none focus:border-cv-interactive ${isAgeNeedsConfirmation ? 'border-amber-500/30 text-cv-warning/80 bg-amber-500/5' : 'border-cv-border'}`}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Role</label>
                            <input 
                              type="text" 
                              value={char.role || ''} 
                              onChange={(e) => updateCharacter(char.name, { role: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none focus:border-cv-interactive"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Faction / Group</label>
                            <input 
                              type="text" 
                              value={char.faction || ''} 
                              onChange={(e) => updateCharacter(char.name, { faction: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none focus:border-cv-interactive"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Current Location</label>
                            <input 
                              type="text" 
                              value={char.location || ''} 
                              onChange={(e) => updateCharacter(char.name, { location: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none focus:border-cv-interactive"
                            />
                          </div>
                        </div>

                        <div className="space-y-2 text-xs">
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Motivation</label>
                            <textarea 
                              value={char.motivation || ''} 
                              onChange={(e) => updateCharacter(char.name, { motivation: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive min-h-[40px] resize-none"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Fear / Wound</label>
                            <textarea 
                              value={char.fear || ''} 
                              onChange={(e) => updateCharacter(char.name, { fear: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive min-h-[40px] resize-none"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Relationship Notes</label>
                            <textarea 
                              value={char.relationship || ''} 
                              onChange={(e) => updateCharacter(char.name, { relationship: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive min-h-[40px] resize-none"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Abilities</label>
                              <input 
                                type="text" 
                                value={char.abilities || ''} 
                                onChange={(e) => updateCharacter(char.name, { abilities: e.target.value })}
                                className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Limitations</label>
                              <input 
                                type="text" 
                                value={char.limitations || ''} 
                                onChange={(e) => updateCharacter(char.name, { limitations: e.target.value })}
                                className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Canon Locks</label>
                              <input 
                                type="text" 
                                value={char.canon_locks || ''} 
                                onChange={(e) => updateCharacter(char.name, { canon_locks: e.target.value })}
                                className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                                placeholder="Unbreakable truths..."
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Forbidden Changes</label>
                              <input 
                                type="text" 
                                value={char.forbidden_changes || ''} 
                                onChange={(e) => updateCharacter(char.name, { forbidden_changes: e.target.value })}
                                className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                                placeholder="Forbidden modifications..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 4. Factions & Groups */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center border-b border-cv-border pb-2">
                <h2 className="text-xs font-black text-cv-interactive uppercase tracking-widest flex items-center gap-2">
                  <span>4. Factions & Groups</span>
                  <span className="text-[9px] bg-cyan-900/30 text-cv-interactive border border-cyan-800/50 px-2 py-0.5 rounded font-mono font-bold">{factionsList.length} total</span>
                </h2>
                <button 
                  onClick={addFaction}
                  className="px-3 py-1 bg-cyan-950/50 hover:bg-cyan-900/50 text-cv-interactive border border-cyan-800/50 hover:border-cyan-600 rounded text-[10px] font-bold uppercase transition-all flex items-center gap-1.5"
                >
                  <Plus size={12} /> Add Faction
                </button>
              </div>

              {factionsList.length === 0 ? (
                <div className="text-center py-6 text-cv-muted text-xs font-mono">No factions/groups created yet. Add one to define story affiliations.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {factionsList.map((faction: any) => (
                    <div key={faction.name} className="bg-cv-panel border border-cv-border rounded-xl p-4 space-y-3 relative group">
                      <button 
                        onClick={() => deleteFaction(faction.name)}
                        className="absolute top-4 right-4 text-cv-danger/50 hover:text-cv-danger opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-500/10"
                        title="Delete Faction"
                      >
                        <Trash2 size={14} />
                      </button>

                      <input 
                        type="text" 
                        value={faction.name} 
                        onChange={(e) => {
                          const newName = e.target.value;
                          if (newName && newName !== faction.name) {
                            const updatedFactions = { ...((ledger as any).factions || {}) };
                            const oldData = updatedFactions[faction.name];
                            delete updatedFactions[faction.name];
                            updatedFactions[newName] = { ...oldData, name: newName };
                            onUpdate(storyBible, { ...ledger, factions: updatedFactions } as any);
                          }
                        }}
                        className="bg-transparent text-white font-black text-sm focus:outline-none focus:border-b focus:border-cv-interactive w-full"
                      />

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Goal</label>
                          <input 
                            type="text" 
                            value={faction.goal || ''} 
                            onChange={(e) => updateFaction(faction.name, { goal: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Leader</label>
                          <input 
                            type="text" 
                            value={faction.leader || ''} 
                            onChange={(e) => updateFaction(faction.name, { leader: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Public Belief</label>
                          <input 
                            type="text" 
                            value={faction.public_belief || ''} 
                            onChange={(e) => updateFaction(faction.name, { public_belief: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Hidden Truth</label>
                          <input 
                            type="text" 
                            value={faction.hidden_truth || ''} 
                            onChange={(e) => updateFaction(faction.name, { hidden_truth: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="space-y-2 text-xs">
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Operational Methods</label>
                          <input 
                            type="text" 
                            value={faction.methods || ''} 
                            onChange={(e) => updateFaction(faction.name, { methods: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-cv-secondary focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Relationship to Protagonist</label>
                          <input 
                            type="text" 
                            value={faction.relationship_to_protagonist || ''} 
                            onChange={(e) => updateFaction(faction.name, { relationship_to_protagonist: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-cv-secondary focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Canon Locks</label>
                          <input 
                            type="text" 
                            value={faction.canon_locks || ''} 
                            onChange={(e) => updateFaction(faction.name, { canon_locks: e.target.value })}
                            className="bg-cv-page border border-cv-border rounded p-1.5 text-xs text-white focus:outline-none font-mono"
                            placeholder="Hard-coded lore rules..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 5. World Rules & Lore */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center border-b border-cv-border pb-2">
                <h2 className="text-xs font-black text-cv-warning uppercase tracking-widest flex items-center gap-2">
                  <span>5. World Rules & Lore</span>
                  <span className="text-[9px] bg-amber-900/30 text-cv-warning border border-amber-800/50 px-2 py-0.5 rounded font-mono font-bold">{worldRulesList.length} total</span>
                </h2>
                <button 
                  onClick={addWorldRule}
                  className="px-3 py-1 bg-amber-950/50 hover:bg-amber-900/50 text-cv-warning border border-amber-800/50 hover:border-amber-600 rounded text-[10px] font-bold uppercase transition-all flex items-center gap-1.5"
                >
                  <Plus size={12} /> Add Rule/Lore
                </button>
              </div>

              {worldRulesList.length === 0 ? (
                <div className="text-center py-6 text-cv-muted text-xs font-mono">No world rules configured yet. Define laws of magic, science, or politics.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {worldRulesList.map((rule: any) => (
                    <div key={rule.title} className="bg-cv-panel border border-cv-border rounded-xl p-4 space-y-3 relative group">
                      <button 
                        onClick={() => deleteWorldRule(rule.title)}
                        className="absolute top-4 right-4 text-cv-danger/50 hover:text-cv-danger opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-500/10"
                        title="Delete Rule"
                      >
                        <Trash2 size={14} />
                      </button>

                      <input 
                        type="text" 
                        value={rule.title} 
                        onChange={(e) => {
                          const newTitle = e.target.value;
                          if (newTitle && newTitle !== rule.title) {
                            const updatedRules = { ...((ledger as any).worldRules || {}) };
                            const oldData = updatedRules[rule.title];
                            delete updatedRules[rule.title];
                            updatedRules[newTitle] = { ...oldData, title: newTitle };
                            onUpdate(storyBible, { ...ledger, worldRules: updatedRules } as any);
                          }
                        }}
                        className="bg-transparent text-white font-black text-sm focus:outline-none focus:border-b focus:border-cv-warning w-full"
                      />

                      <div className="flex flex-col gap-1 text-xs">
                        <textarea 
                          value={rule.content || ''} 
                          onChange={(e) => updateWorldRule(rule.title, { content: e.target.value })}
                          className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-warning min-h-[80px]"
                          placeholder="State exact limits, magical parameters, taboos..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 6. Writing Style Rules */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <h2 className="text-xs font-black text-cv-success uppercase tracking-widest border-b border-cv-border pb-2">
                6. Writing Style Rules
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Narrative Tone & Prose Style</label>
                  <textarea 
                    value={getStyleRule('tone_style', 'Gritty, cinematic, third-person limited. Deep interiority.')} 
                    onChange={(e) => updateStyleRule('tone_style', e.target.value)}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:outline-none focus:border-cv-success min-h-[80px]"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Dialogue Style & naturalism</label>
                  <textarea 
                    value={getStyleRule('dialogue_style', 'Dialogue is sharp, filled with subtext, fragments and natural interruptions.')} 
                    onChange={(e) => updateStyleRule('dialogue_style', e.target.value)}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:outline-none focus:border-cv-success min-h-[80px]"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Forbidden Words & Phrases</label>
                  <textarea 
                    value={getStyleRule('forbidden_words', 'shattered, suffocating, visceral, resonance, tapestry')} 
                    onChange={(e) => updateStyleRule('forbidden_words', e.target.value)}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:outline-none focus:border-cv-success min-h-[80px]"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Prose Precision & Constraints (e.g. Modern restrictions, exact measurements)</label>
                  <textarea 
                    value={getStyleRule('constraints', 'No modern terminology or idioms. Keep spatial movement realistic.')} 
                    onChange={(e) => updateStyleRule('constraints', e.target.value)}
                    className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-cv-secondary focus:outline-none focus:border-cv-success min-h-[80px]"
                  />
                </div>
              </div>
            </div>

            {/* 7. Canon Locks */}
            <div className="bg-cv-page/40 border border-cv-border/80 rounded-2xl p-6 space-y-4">
              <h2 className="text-xs font-black text-rose-400 uppercase tracking-widest border-b border-cv-border pb-2">
                7. Canon Locks
              </h2>
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Hard facts the narrative engine must NEVER contradict</label>
                <textarea 
                  value={(storyBible as any).canonLocks || ''} 
                  onChange={(e) => onUpdate({ ...storyBible, canonLocks: e.target.value } as any, ledger)}
                  className="bg-cv-panel border border-cv-border rounded p-3 text-xs text-rose-300 focus:outline-none focus:border-rose-500 min-h-[100px]"
                  placeholder="Review specific canon locks extracted or add custom rules here..."
                />
              </div>
            </div>

            {/* Action panel */}
            <div className="border-t border-cv-border pt-6 flex justify-between items-center bg-cv-page -mx-8 -mb-8 p-8 rounded-b-3xl">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-cv-muted">Vol {storyBible.volume} Blueprint Status</span>
                {storyBible.isBlueprintApproved ? (
                  <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-wider border border-green-500/30">APPROVED</span>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-cv-elevated text-cv-muted text-[10px] font-black uppercase tracking-wider border border-cv-border-strong">PENDING APPROVAL</span>
                )}
              </div>
              <div className="flex gap-4">
                <button 
                  onClick={onApproveBlueprint}
                  className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ${storyBible.isBlueprintApproved ? 'bg-cv-elevated text-cv-muted border border-cv-border-strong' : 'bg-cv-success hover:bg-[#2EB67C] text-white shadow-xl shadow-black/30'}`}
                >
                  {storyBible.isBlueprintApproved ? 'Unapprove Blueprint' : 'Approve Story Blueprint'}
                </button>
                <button 
                  onClick={() => {
                    setActiveTab('roadmap');
                  }}
                  disabled={!storyBible.isBlueprintApproved}
                  className="px-6 py-3 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Proceed to Roadmap
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Chapter Roadmap */}
        {activeTab === 'roadmap' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {roadmapPersistError && (
              <div className="mb-4 rounded-xl border border-red-500/40 bg-red-950/30 px-4 py-3 text-xs text-red-200">
                {roadmapPersistError}
              </div>
            )}
            {roadmapParseError && (
              <div className="bg-rose-950/20 border border-rose-500/30 rounded-2xl p-6 text-center space-y-4">
                <div className="flex justify-center text-rose-500 text-3xl font-black">⚠️</div>
                <h4 className="text-xs font-black text-rose-400 uppercase tracking-wide">Roadmap Parsing Failure</h4>
                <p className="text-xs text-rose-300 font-mono">{roadmapParseError}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button 
                    onClick={handleParseCustomRoadmap}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-black uppercase tracking-wider transition-colors shadow-lg shadow-rose-950/30"
                  >
                    Retry Parsing
                  </button>
                  <button 
                    onClick={() => {
                      alert(storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap || "No raw roadmap text found.");
                    }}
                    className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary border border-cv-border-strong rounded-lg text-xs font-black uppercase tracking-wider transition-colors"
                  >
                    Show Raw Roadmap
                  </button>
                  <button 
                    onClick={() => {
                      setRoadmapParseError(null);
                      updateBible({
                        chapterPlans: [{
                          id: `plan_fallback_${Date.now()}`,
                          chapterNumber: 1,
                          title: "Chapter 1",
                          summary: storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap || "Locked to raw roadmap plan.",
                          requiredBeats: ["needs confirmation"],
                          status: 'planned' as const,
                          source: 'USER_ROADMAP',
                          updatedAt: new Date().toISOString()
                        }],
                        activeRoadmapSource: 'USER_ROADMAP',
                        originalUserRoadmapRaw: storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap,
                      });
                    }}
                    className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary border border-cv-border-strong rounded-lg text-xs font-black uppercase tracking-wider transition-colors"
                  >
                    Use Raw Roadmap as Locked Plan
                  </button>
                  <button
                    onClick={onGenerateRoadmap}
                    disabled={isGeneratingRoadmap}
                    className="px-4 py-2 bg-cv-success hover:bg-[#2EB67C] text-white rounded-lg text-xs font-black uppercase tracking-wider transition-colors"
                  >
                    {isGeneratingRoadmap ? 'Generating...' : 'Generate AI Roadmap'}
                  </button>
                </div>
              </div>
            )}

            {storyBible.roadmapParseWarnings && storyBible.roadmapParseWarnings.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 px-4 py-3 text-xs text-amber-200">
                {storyBible.roadmapParseWarnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
              </div>
            )}

            {unmatchedRoadmapBlocks.length > 0 && (
              <div className="rounded-xl border border-cv-border bg-cv-panel/40 px-4 py-3 text-xs text-cv-secondary space-y-2">
                <p className="font-black uppercase tracking-wider text-cv-warning">Unmatched roadmap blocks</p>
                {unmatchedRoadmapBlocks.map((block, index) => (
                  <pre key={`${index}-${block.slice(0, 24)}`} className="whitespace-pre-wrap text-[10px] text-cv-muted">{block.slice(0, 500)}</pre>
                ))}
              </div>
            )}

            {/* Roadmap Status banner */}
            {hasPremadeRoadmap && (
              <div className="bg-cv-success/10/20 border border-cv-success/30 rounded-2xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-cv-success text-xl font-bold">✨</span>
                  <div>
                    <h4 className="text-xs font-black text-emerald-300 uppercase tracking-wide">Pre-Made Roadmap Detected</h4>
                    <p className="text-[10px] text-cv-success/80 font-mono">The user has provided a custom roadmap template. You can convert it into editable chapter cards below.</p>
                  </div>
                </div>
                {needsPremadeParse && (
                  <button 
                    onClick={handleParseCustomRoadmap}
                    className="px-4 py-2 bg-cv-success hover:bg-[#2EB67C] text-white rounded-lg text-xs font-black uppercase tracking-wider transition-colors shadow-lg shadow-emerald-950/30"
                  >
                    Convert Roadmap
                  </button>
                )}
              </div>
            )}

            {!hasPremadeRoadmap && chapterPlans.length === 0 && !roadmapParseError && (
              <div className="bg-amber-950/10 border border-amber-500/20 rounded-2xl p-6 text-center space-y-4">
                <p className="text-xs text-cv-warning font-mono">No roadmap has been synthesized or uploaded yet.</p>
                <p className="text-[10px] text-cv-muted max-w-md mx-auto">Click below to generate the Chapter Roadmap based on your approved Story Blueprint, or create individual chapter cards manually.</p>
                <div className="flex justify-center gap-4">
                  <button 
                    onClick={onGenerateRoadmap}
                    disabled={isGeneratingRoadmap}
                    className="px-6 py-3 bg-cv-success hover:bg-[#2EB67C] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-xl shadow-black/30 flex items-center gap-2"
                  >
                    {isGeneratingRoadmap ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Generating Roadmap...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> Generate AI Roadmap
                      </>
                    )}
                  </button>
                  <button 
                    onClick={addChapterCard}
                    className="px-6 py-3 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary border border-cv-border-strong text-xs font-black uppercase tracking-widest rounded-xl transition-all"
                  >
                    Create Manual Card
                  </button>
                </div>
              </div>
            )}

            {/* Chapter plans list */}
            {chapterPlans.length > 0 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-cv-border pb-2">
                  <h2 className="text-xs font-black text-cv-interactive uppercase tracking-widest flex items-center gap-2">
                    <span>Chapter Cards Checklist</span>
                    <span className="text-[9px] bg-cyan-900/30 text-cv-interactive border border-cyan-800/50 px-2 py-0.5 rounded font-mono font-bold">{chapterPlans.length} plans</span>
                  </h2>
                  <div className="flex gap-2">
                    <button 
                      onClick={addChapterCard}
                      className="px-3 py-1.5 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary border border-cv-border-strong rounded text-[10px] font-bold uppercase transition-all flex items-center gap-1"
                    >
                      <Plus size={12} /> Add Chapter Plan
                    </button>
                    <button 
                      onClick={() => {
                        if (window.confirm("Are you sure you want to completely regenerate the roadmap? All manual edits will be lost.")) {
                          onGenerateRoadmap();
                        }
                      }}
                      disabled={isGeneratingRoadmap}
                      className="px-3 py-1.5 bg-pink-950/40 hover:bg-pink-900/40 text-pink-400 border border-pink-900/30 rounded text-[10px] font-bold uppercase transition-all flex items-center gap-1"
                    >
                      {isGeneratingRoadmap ? 'Regenerating...' : 'Regenerate Roadmap'}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {chapterPlans.map((plan, index) => {
                    const isExpanded = expandedSection === plan.id;
                    return (
                      <div key={plan.id} className={`border ${isExpanded ? 'border-cv-interactive/50' : 'border-cv-border'} rounded-xl bg-cv-panel/50 overflow-hidden transition-all`}>
                        {/* Header bar */}
                        <div className="flex items-center p-3 gap-3 bg-cv-panel/80">
                          <button 
                            onClick={() => setExpandedSection(isExpanded ? null : plan.id)} 
                            className="text-cv-muted hover:text-white"
                          >
                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </button>

                          <span className="text-cv-gold font-mono font-black text-xs mr-3">Chapter {plan.chapterNumber}: {plan.title || "Needs title"}</span>
                          {isExpanded && (
                            <input 
                              type="text" 
                              value={plan.title} 
                              placeholder="Needs title"
                              onChange={(e) => updateChapterCard(index, { title: e.target.value })}
                              className="bg-cv-page border border-cv-border rounded px-2 py-1 text-white font-black text-xs focus:outline-none focus:border-cv-interactive flex-1 truncate max-w-sm"
                            />
                          )}
                          <div className="flex items-center gap-1.5">
                            <button 
                              onClick={() => moveChapterUp(index)} 
                              disabled={index === 0} 
                              className="p-1 text-cv-muted hover:text-white hover:bg-cv-elevated disabled:opacity-30 rounded"
                            >
                              <ArrowUp size={14} />
                            </button>
                            <button 
                              onClick={() => moveChapterDown(index)} 
                              disabled={index === chapterPlans.length - 1} 
                              className="p-1 text-cv-muted hover:text-white hover:bg-cv-elevated disabled:opacity-30 rounded"
                            >
                              <ArrowDown size={14} />
                            </button>
                            <button 
                              onClick={() => deleteChapterCard(index)}
                              className="p-1 text-cv-danger/50 hover:text-cv-danger hover:bg-red-500/10 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Collapsible Details */}
                        {isExpanded && (
                          <div className="p-4 border-t border-cv-border flex flex-col gap-4 text-xs">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">POV Character</label>
                                <input 
                                  type="text" 
                                  value={plan.pov || ''} 
                                  onChange={(e) => updateChapterCard(index, { pov: e.target.value })}
                                  className="bg-cv-page border border-cv-border rounded p-2 text-xs text-white focus:outline-none"
                                />
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Location</label>
                                <input 
                                  type="text" 
                                  value={(plan.locationFocus || []).join(', ')} 
                                  onChange={(e) => updateChapterCard(index, { locationFocus: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                                  placeholder="e.g. Scavenger Camp, Ruined Arch"
                                  className="bg-cv-page border border-cv-border rounded p-2 text-xs text-white focus:outline-none"
                                />
                              </div>
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Emotional Shift or Climax</label>
                              <input 
                                type="text" 
                                value={(plan as any).emotionalShift || ''} 
                                onChange={(e) => updateChapterCard(index, { emotionalShift: e.target.value } as any)}
                                placeholder="e.g. Cynical despair shifts to flickering hope..."
                                className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none"
                              />
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Ending Hook</label>
                              <input 
                                type="text" 
                                value={(plan as any).endingHook || ''} 
                                onChange={(e) => updateChapterCard(index, { endingHook: e.target.value } as any)}
                                placeholder="e.g. The alarm sounds, plunging the camp into absolute chaos..."
                                className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none"
                              />
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Chapter Summary</label>
                              <textarea 
                                value={plan.summary} 
                                onChange={(e) => updateChapterCard(index, { summary: e.target.value })}
                                className="bg-cv-page border border-cv-border rounded p-3 text-xs text-cv-secondary focus:outline-none min-h-[60px]"
                              />
                            </div>

                            {/* Required beats checklist */}
                            <div className="flex flex-col gap-2">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Required Beats</label>
                              <div className="flex flex-col gap-1.5">
                                {(plan.requiredBeats || []).map((beat, bIdx) => (
                                  <div key={bIdx} className="flex gap-2">
                                    <textarea 
                                      value={beat} 
                                      onChange={(e) => updateChapterStringArray(index, 'requiredBeats', bIdx, e.target.value)}
                                      className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none flex-1 min-h-[30px]"
                                    />
                                    <button 
                                      onClick={() => removeChapterStringArrayItem(index, 'requiredBeats', bIdx)}
                                      className="text-cv-danger hover:text-cv-danger p-1 hover:bg-red-500/10 rounded self-center"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))}
                                <button 
                                  onClick={() => addChapterStringArrayItem(index, 'requiredBeats')}
                                  className="text-[10px] font-bold uppercase text-cv-gold hover:text-cv-interactive self-start flex items-center gap-1"
                                >
                                  <Plus size={10} /> Add Beat
                                </button>
                              </div>
                            </div>

                            {/* Forbidden moves */}
                            <div className="flex flex-col gap-2">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Forbidden Moves</label>
                              <div className="flex flex-col gap-1.5">
                                {(plan.forbiddenMoves || []).map((move, mIdx) => (
                                  <div key={mIdx} className="flex gap-2">
                                    <input 
                                      type="text"
                                      value={move} 
                                      onChange={(e) => updateChapterStringArray(index, 'forbiddenMoves', mIdx, e.target.value)}
                                      className="bg-cv-page border border-cv-border rounded p-2 text-xs text-rose-400/80 focus:outline-none flex-1"
                                    />
                                    <button 
                                      onClick={() => removeChapterStringArrayItem(index, 'forbiddenMoves', mIdx)}
                                      className="text-cv-danger hover:text-cv-danger p-1 hover:bg-red-500/10 rounded"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))}
                                <button 
                                  onClick={() => addChapterStringArrayItem(index, 'forbiddenMoves')}
                                  className="text-[10px] font-bold uppercase text-rose-400 hover:text-rose-300 self-start flex items-center gap-1"
                                >
                                  <Plus size={10} /> Add Forbidden Move
                                </button>
                              </div>
                            </div>

                            {/* Continuity/Canon Notes */}
                            <div className="flex flex-col gap-2">
                              <label className="text-[9px] font-bold text-cv-muted uppercase font-mono">Continuity & Canon Notes</label>
                              <div className="flex flex-col gap-1.5">
                                {(plan.continuityNotes || []).map((note, nIdx) => (
                                  <div key={nIdx} className="flex gap-2">
                                    <textarea 
                                      value={note} 
                                      onChange={(e) => updateChapterStringArray(index, 'continuityNotes', nIdx, e.target.value)}
                                      className="bg-cv-page border border-cv-border rounded p-2 text-xs text-cv-secondary focus:outline-none flex-1 min-h-[30px]"
                                    />
                                    <button 
                                      onClick={() => removeChapterStringArrayItem(index, 'continuityNotes', nIdx)}
                                      className="text-cv-danger hover:text-cv-danger p-1 hover:bg-red-500/10 rounded self-center"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))}
                                <button 
                                  onClick={() => addChapterStringArrayItem(index, 'continuityNotes')}
                                  className="text-[10px] font-bold uppercase text-cv-warning hover:text-orange-300 self-start flex items-center gap-1"
                                >
                                  <Plus size={10} /> Add Continuity Note
                                </button>
                              </div>
                            </div>

                            {/* Raw Roadmap Preview */}
                            {(plan as any).rawText && (
                              <div className="flex flex-col gap-2 mt-2 border-t border-cv-border/60 pt-3">
                                <details className="group">
                                  <summary className="text-[9px] font-bold text-cv-muted uppercase font-mono cursor-pointer hover:text-cv-secondary flex items-center gap-1 select-none">
                                    <span>🔍 Raw Roadmap Preview</span>
                                  </summary>
                                  <pre className="mt-2 bg-cv-page/80 border border-cv-border/80 rounded p-3 text-[10px] font-mono text-cv-muted whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
                                    {(plan as any).rawText}
                                  </pre>
                                </details>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action panel */}
            <div className="border-t border-cv-border pt-6 flex justify-between items-center bg-cv-page -mx-8 -mb-8 p-8 rounded-b-3xl">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-cv-muted">Chapter Roadmap Status</span>
                {storyBible.isRoadmapApproved ? (
                  <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-wider border border-green-500/30">APPROVED</span>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-cv-elevated text-cv-muted text-[10px] font-black uppercase tracking-wider border border-cv-border-strong">PENDING APPROVAL</span>
                )}
              </div>
              <div className="flex gap-4">
                <button 
                  onClick={onApproveRoadmap}
                  disabled={chapterPlans.length === 0}
                  className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ${storyBible.isRoadmapApproved ? 'bg-cv-elevated text-cv-muted border border-cv-border-strong hover:border-cv-border-strong hover:text-white' : 'bg-cyan-600 hover:bg-cv-gold text-white shadow-xl shadow-cyan-900/30 disabled:opacity-30'}`}
                >
                  {storyBible.isRoadmapApproved ? 'Unapprove Roadmap' : 'Approve Chapter Roadmap'}
                </button>
                <button 
                  onClick={() => void onEnterNarrativeHub()}
                  disabled={!storyBible.isBlueprintApproved || !storyBible.isRoadmapApproved || isEnteringHub}
                  className="px-6 py-3 bg-cv-success hover:bg-[#2EB67C] disabled:bg-cv-elevated text-white disabled:text-cv-disabled rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:cursor-not-allowed shadow-xl shadow-blue-900/20"
                >
                  {isEnteringHub ? 'Saving Roadmap...' : 'Enter Narrative Hub'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
